import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useCurrentReservationList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refCurrentReservationsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'lot',
      label: 'Product & Lot Type',
      sortable: true,
    },
    {
      key: 'zone',
      label: 'Zone &  Lot No.',
    },
    {
      key: 'status',
      label: 'Reservation Status',
    },
    {
      key: 'reservationFor',
      label: 'Reserved for',
      sortable: true,
    },
    {
      key: 'agent',
      label: 'Agent',
      sortable: true,
    },
    {
      key: 'reservationStartDate',
      label: 'Reserved period',
      sortable: false,
    },
    {
      key: 'reservationEndDate',
      label: 'Time left',
      sortable: true,
    },
    {
      key: 'createdBy',
      label: 'created by',
      sortable: true,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const agentSearchQuery = ref(route.query.agentSearch ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc !== 'false')
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const productFilter = ref([])
  if (Array.isArray(route.query.product)) {
    productFilter.value = route.query.product
  } else if (route.query.product) {
    productFilter.value = [route.query.product]
  }
  const lotTypeFilter = ref([])
  if (Array.isArray(route.query.reservable)) {
    lotTypeFilter.value = route.query.reservable
  } else if (route.query.reservable) {
    lotTypeFilter.value = [route.query.reservable]
  }
  const toDeletedID = ref(null)
  const type = ref('submission-hold')
  const reservationData = ref([])
  const currentDataLength = ref(0)
  const totalReservations = ref(0)
  const createdAtFilter = ref(route.query.createdAt || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refCurrentReservationsTable.value ? refCurrentReservationsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalReservations.value,
    }
  })

  const refetchData = () => {
    refCurrentReservationsTable.value.refresh()
  }

  const fetchReservations = () => {
    store
      .dispatch('app-current-reservations/fetchReservations', {
        search: searchQuery.value,
        agentSearch: agentSearchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        product: productFilter.value,
        lotType: lotTypeFilter.value,
        status: statusFilter.value,
        createdAt: createdAtFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { reservations, total } = response.data
        reservationData.value = reservations
        currentDataLength.value = reservations.length
        totalReservations.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              agentSearch: agentSearchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              product: productFilter.value,
              lotType: lotTypeFilter.value,
              status: statusFilter.value,
              createdAt: createdAtFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([statusFilter, createdAtFilter, productFilter, lotTypeFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchReservations()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchReservations()
      }
    }, 1000)
  }, { deep: true })

  watch([agentSearchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchReservations()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchReservations()
  }, { deep: true })

  fetchReservations()

  const deleteReservation = () => {
    store
      .dispatch('app-current-reservations/deleteReservation', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        fetchReservations()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveReservationStatusVariant = status => {
    if (status === 'Reserved') return 'warning'
    if (status === 'Staff Hold') return 'danger'
    if (status === 'Submission Hold') return 'danger'
    if (status === 'Processing') return 'info'
    return 'primary'
  }

  return {
    fetchReservations,
    tableColumns,
    perPage,
    currentPage,
    totalReservations,
    dataMeta,
    perPageOptions,
    searchQuery,
    agentSearchQuery,
    sortBy,
    isSortDirDesc,
    refCurrentReservationsTable,

    resolveReservationStatusVariant,
    refetchData,

    toDeletedID,
    deleteReservation,
    reservationData,

    // Extra Filters
    statusFilter,
    productFilter,
    lotTypeFilter,
    createdAtFilter,
  }
}
